import * as THREE from 'three';
import { DDSLoader } from 'three/addons/loaders/DDSLoader.js';
import { parseGHG, decodeGHGMesh, evaluateBindPose } from './ghg.mjs';
import { sampleAN3 } from './an3.mjs';
import { evaluateAN3Pose, blendAN3Samples } from './pose.mjs';

// The browser adapter keeps the original mesh streams, palettes and inverse
// bind matrices. Character and scene coordinates are reflected together in X.
export function createCharacter(buffer, config, animations) {
  const parsed = parseGHG(buffer), root = new THREE.Group(), model = new THREE.Group();
  // DrawGameObjectsProcess (render.cpp:2672) uses facing + NUANG_180DEG.
  model.rotation.y = Math.PI;
  root.add(model);
  const bones = parsed.joints.map(() => new THREE.Bone());
  for (const bone of bones) { bone.matrixAutoUpdate = false; model.add(bone); }
  const skeleton = new THREE.Skeleton(bones, parsed.joints.map(j => new THREE.Matrix4().fromArray(j.inverseBindMatrix)));
  const textures = new Map(), materials = new Map(), geometries = [], rigid = [];
  let currentMatrices = evaluateBindPose(parsed);
  const dds = new DDSLoader();
  const selectedLayers = new Set(config.layers.special ?? config.layers.high ?? [0]);
  const textureFor = index => {
    if (textures.has(index)) return textures.get(index);
    const entry = parsed.textures.get(index); if (!entry) return null;
    const data = dds.parse(entry.data, true);
    if (!data.format || !data.mipmaps.length || data.isCubemap) throw new Error('Unsupported character texture.');
    const texture = data.format === THREE.RGBAFormat
      ? new THREE.DataTexture(data.mipmaps[0].data, data.width, data.height, THREE.RGBAFormat)
      : new THREE.CompressedTexture(data.mipmaps, data.width, data.height, data.format);
    texture.flipY = false; texture.colorSpace = THREE.SRGBColorSpace;
    texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
    texture.needsUpdate = true; textures.set(index, texture); return texture;
  };
  for (const draw of parsed.draws) {
    // The second render collection contains facial/deformer surfaces too;
    // it is drawn alongside the first collection, rather than replacing it.
    if (!selectedLayers.has(draw.layer)) continue;
    const decoded = decodeGHGMesh(parsed, draw), geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(decoded.positions, 3));
    geometry.setAttribute('normal', new THREE.BufferAttribute(decoded.normals, 3));
    geometry.setAttribute('uv', new THREE.BufferAttribute(decoded.uvs, 2));
    geometry.setAttribute('color', new THREE.BufferAttribute(decoded.colors, 3));
    geometry.setIndex(new THREE.BufferAttribute(decoded.indices, 1));
    if (!decoded.hasNormals) geometry.computeVertexNormals();
    const source = draw.material;
    let material = materials.get(source.address);
    if (!material) {
      const map = textureFor(source.texture), alpha = source.alpha & 15;
      material = new THREE.MeshLambertMaterial({ map, color: map ? 0xffffff : new THREE.Color(...source.color.slice(0, 3)),
        vertexColors: decoded.hasColors, alphaTest: 0.015, transparent: alpha >= 1 && alpha <= 3 });
      materials.set(source.address, material);
    }
    let mesh;
    if (draw.smooth) {
      geometry.setAttribute('skinIndex', new THREE.BufferAttribute(decoded.skinIndices, 4));
      geometry.setAttribute('skinWeight', new THREE.BufferAttribute(decoded.skinWeights, 4));
      mesh = new THREE.SkinnedMesh(geometry, material);
      mesh.bind(skeleton, new THREE.Matrix4());
    } else {
      mesh = new THREE.Mesh(geometry, material); mesh.matrixAutoUpdate = false;
      rigid.push({ mesh, draw });
    }
    mesh.frustumCulled = false; mesh.name = `${parsed.layers[draw.layer].name}:${draw.mesh.address}`;
    model.add(mesh); geometries.push(geometry);
  }
  function applyMatrices(matrices) {
    currentMatrices = matrices;
    bones.forEach((bone, i) => bone.matrix.fromArray(matrices[i]));
    for (const { mesh, draw } of rigid) mesh.matrix.fromArray(matrices[draw.boneIndex]).multiply(new THREE.Matrix4().fromArray(draw.matrix));
    root.updateMatrixWorld(true);
  }
  applyMatrices(evaluateBindPose(parsed));
  return {
    root, model, parsed, config, animations, skeleton,
    get matrices() { return currentMatrices; },
    pose(clip, frame, blend = null) {
      const animation = animations.get(clip.toLowerCase());
      if (!animation) throw new Error(`Missing original animation: ${clip}`);
      let sampled = sampleAN3(animation, frame);
      if (blend) sampled = blendAN3Samples(sampleAN3(animations.get(blend.clip), blend.frame), sampled, blend.alpha);
      const pose = evaluateAN3Pose(parsed, sampled);
      applyMatrices(pose.worldMatrices); return pose;
    },
    dispose() { root.removeFromParent(); geometries.forEach(g => g.dispose()); materials.forEach(m => m.dispose()); textures.forEach(t => t.dispose()); skeleton.dispose(); },
    summary: { ...parsed.summary, renderedMeshes: geometries.length, selectedLayers: [...selectedLayers] },
  };
}
